/**
 * SubjectAggregatePanel — one visual container for every widget on
 * a course page whose numbers describe the WHOLE SUBJECT nationally
 * (CAH aggregate, national baseline, sector-cluster survey) rather
 * than this specific course cohort.
 *
 * Signals used, consistent across every use-site:
 *   • Dashed stone border on a warm stone-50/70 fill so the panel
 *     reads as a "sidebar detour" from the course-specific stack.
 *   • Floating pill top-right: `SUBJECT · <label>` (small-caps,
 *     amber dot) — visible even mid-scroll.
 *   • "Zoom out:" prefix and eyebrow inside the panel head.
 *   • "Back to this course ↓" dashed-divider footer that closes the
 *     detour and points the eye back to the following course blocks.
 *
 * Reach for this instead of hand-rolling the pattern. Anywhere it's
 * used, parents get the same "we've stepped out to subject data,
 * we're stepping back now" rhythm.
 */
export default function SubjectAggregatePanel({
  subjectLabel,
  eyebrow,
  zoomOutCopy,
  children,
  testId,
  className = "",
  headerRight = null,
  showFooter = true,
}) {
  return (
    <div
      className={`mt-6 rounded-xl border border-dashed border-stone-300 bg-stone-50/70 p-5 relative ${className}`}
      data-testid={testId}
      data-scope="subject-aggregate"
    >
      <div className="absolute -top-2.5 right-4 flex items-center gap-1.5 rounded-full bg-white border border-stone-300 px-2 py-0.5">
        <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
        <span
          className="text-[10px] font-underground-sc tracking-[0.15em] text-stone-600"
          data-testid={testId ? `${testId}-scope-pill` : undefined}
        >
          Subject{subjectLabel ? ` · ${subjectLabel}` : ""}
        </span>
      </div>

      {(eyebrow || zoomOutCopy || headerRight) && (
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
          <div>
            {eyebrow && (
              <div className="label-uppercase !text-[10px] text-stone-500">
                {eyebrow}
              </div>
            )}
            {zoomOutCopy && (
              <div className="text-sm text-stone-700 mt-1">
                <span className="font-underground-sc tracking-[0.12em] text-amber-800 text-[11px] mr-1.5">
                  Zoom out:
                </span>
                {zoomOutCopy}
              </div>
            )}
          </div>
          {headerRight}
        </div>
      )}

      {children}

      {showFooter && (
        <div
          className="mt-4 pt-3 border-t border-dashed border-stone-300 flex items-center gap-1.5 text-[10px] font-underground-sc tracking-[0.14em] text-stone-500"
          data-testid={testId ? `${testId}-zoom-back` : undefined}
        >
          <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Back to this course
          <span aria-hidden className="text-stone-400 ml-auto tracking-normal font-editorial text-[11px]">↓</span>
        </div>
      )}
    </div>
  );
}
